import React from "react";
import { graphql, PageProps } from "gatsby";
import Layout from "components/Layout";
import HeroSection from "components/homepageSections/HeroSection";
import MentionsTicker from "components/homepageSections/MentionsTicker";
import Features from "components/homepageSections/Features";
import WhyEnte from "components/homepageSections/WhyEnte";
import ExperienceEnte from "components/homepageSections/ExperienceEnte";
import CustomerReviews from "components/homepageSections/CustomerReviews";
import Pricing from "components/homepageSections/Pricing";
import MemoriesPreserved from "components/homepageSections/MemoriesPreserved";
import FAQs from "components/homepageSections/FAQs";
import "styles/landing.css";
import PrivateDucky from "images/homepage/PrivateDucky";
import ReliableDucky from "images/homepage/ReliableDucky";
import DurableDucky from "images/homepage/DurableDucky";
import EU from "images/homepage/EU";
import { FluidImageData } from "types";
import { Trans, useTranslation } from "react-i18next";
import { IReview } from "data/reviews";
import reviews from "data/reviews";
import i18n from "../../i18n";

interface IPageData {
    featureImage: FluidImageData;
    feature1: FluidImageData;
    feature2: FluidImageData;
    feature3: FluidImageData;
    feature4: FluidImageData;
}

interface HeroSectionProps {
    headingText: string;
    paragraphText: string;
    buttonText: string;
    backdropBackground?: string;
    backdropBackgroundImage?: string;
    duckyShieldColor: string;
}

interface ExperienceEnteProps {
    background: string;
    heading: string;
    subheading: string;
    icon: string;
    language: string;
}

const IndexTemplate: React.FC<PageProps<IPageData, { language: string }>> = ({
    path,
    pageContext,
    data: { featureImage, feature1, feature2, feature3, feature4 },
}) => {
    const { language } = pageContext;

    if (i18n.language !== language) {
        i18n.changeLanguage(language);
    }

    const { t } = useTranslation();

    const heroSectionProps: HeroSectionProps = {
        headingText: "",
        paragraphText: t("EP_INDEX_HERO_DESCRIPTION"),
        buttonText: t("EP_INDEX_HERO_DOWNLOAD_BUTTON"),
        backdropBackgroundImage: `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABaWlDQ1BEaXNwbGF5IFAzAAB4nHWQvUvDUBTFT6tS0DqIDh0cMolD1NIKdnFoKxRFMFQFq1OafgltfCQpUnETVyn4H1jBWXCwiFRwcXAQRAcR3Zw6KbhoeN6XVNoi3sfl/Ticc7lcwBtQGSv2AijplpFMxKS11Lrke4OHnlOqZrKooiwK/v276/PR9d5PiFlNu3YQ2U9cl84ul3aeAlN//V3Vn8maGv3f1EGNGRbgkYmVbYsJ3iUeMWgp4qrgvMvHgtMunzuelWSc+JZY0gpqhrhJLKc79HwHl4plrbWD2N6f1VeXxRzqUcxhEyYYilBRgQQF4X/8044/ji1yV2BQLo8CLMpESRETssTz0KFhEjJxCEHqkLhz634PrfvJbW3vFZhtcM4v2tpCAzidoZPV29p4BBgaAG7qTDVUR+qh9uZywPsJMJgChu8os2HmwiF3e38M6Hvh/GMM8B0CdpXzryPO7RqFn4Er/QcXKWq8MSlPPgAAABBJREFUeAEBBQD6/wAIwiX/A7QB7/maltEAAAAASUVORK5CYII=`,
        duckyShieldColor: "#3EB54A",
    };

    const FeaturesSectionProps = {
        heading: t("EP_INDEX_FEATURES_TITLE"),
        features: [
            {
                title: t("EP_INDEX_FEATURES_SEARCH_TITLE"),
                description: t("EP_INDEX_FEATURES_SEARCH_DESC"),
                alt: t("EP_INDEX_FEATURES_SEARCH_ALT"),
                image: {
                    srcSetWebp: feature1.fluid.srcSetWebp,
                    aspectRatio: feature1.fluid.aspectRatio,
                },
            },
            {
                title: t("EP_INDEX_FEATURES_MEMORIES_TITLE"),
                description: t("EP_INDEX_FEATURES_MEMORIES_DESC"),
                alt: t("EP_INDEX_FEATURES_MEMORIES_ALT"),
                image: {
                    srcSetWebp: feature2.fluid.srcSetWebp,
                    aspectRatio: feature2.fluid.aspectRatio,
                },
            },
            {
                title: t("EP_INDEX_FEATURES_COLLAB_TITLE"),
                description: t("EP_INDEX_FEATURES_COLLAB_DESC"),
                alt: t("EP_INDEX_FEATURES_COLLAB_ALT"),
                image: {
                    srcSetWebp: feature3.fluid.srcSetWebp,
                    aspectRatio: feature3.fluid.aspectRatio,
                },
            },
            {
                title: t("EP_INDEX_FEATURES_FAMILY_TITLE"),
                description: t("EP_INDEX_FEATURES_FAMILY_DESC"),
                alt: t("EP_INDEX_FEATURES_FAMILY_ALT"),
                image: {
                    srcSetWebp: feature4.fluid.srcSetWebp,
                    aspectRatio: feature4.fluid.aspectRatio,
                },
            },
        ],
    };

    const whyEnteProps = {
        heading: t("EP_INDEX_WHY_ENTE_TITLE"),
        descriptionDesktop: (
            <Trans i18nKey="EP_INDEX_WHY_ENTE_DESCRIPTION_DESKTOP">
                End-to-end encryption, durable storage and simple sharing.
                <br />
                We have packed these and much more into our beautiful open
                source apps.
            </Trans>
        ),
        descriptionMobile: (
            <Trans i18nKey="EP_INDEX_WHY_ENTE_DESCRIPTION_MOBILE">
                End-to-end encryption, durable storage and simple sharing. We
                have packed these and much more into our beautiful open source
                apps.
            </Trans>
        ),
        points: [
            {
                heading: t("EP_INDEX_WHY_ENTE_PRIVATE_TITLE"),
                image: <PrivateDucky />,
                content: [
                    <Trans
                        i18nKey="EP_INDEX_WHY_ENTE_PRIVATE_TEXT_1"
                        components={{
                            1: <a href="/architecture" />,
                            2: <strong />,
                        }}
                    />,
                    <Trans i18nKey="EP_INDEX_WHY_ENTE_PRIVATE_TEXT_2" />,
                ],
            },
            {
                heading: t("EP_INDEX_WHY_ENTE_DURABLE_TITLE"),
                image: <DurableDucky />,
                content: [
                    <Trans
                        i18nKey="EP_INDEX_WHY_ENTE_DURABLE_TEXT_1"
                        components={{
                            1: <a href="https://github.com/ente-io" />,
                            2: <strong />,
                        }}
                    />,
                    <Trans
                        i18nKey="EP_INDEX_WHY_ENTE_DURABLE_TEXT_2"
                        components={{
                            1: <a href="/reliability" />,
                            2: <strong />,
                            eu: <EU />,
                        }}
                    />,
                ],
            },
            {
                heading: t("EP_INDEX_WHY_ENTE_RELIABLE_TITLE"),
                image: <ReliableDucky />,
                content: [
                    <Trans i18nKey="EP_INDEX_WHY_ENTE_RELIABLE_TEXT_1" />,
                    <Trans
                        i18nKey="EP_INDEX_WHY_ENTE_RELIABLE_TEXT_2"
                        components={{
                            1: <a href="mailto:human@ente.io" />,
                            2: <strong />,
                        }}
                    />,
                ],
            },
        ],
    };

    const experienceEnteProps: ExperienceEnteProps = {
        background: `url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABaWlDQ1BEaXNwbGF5IFAzAAB4nHWQvUvDUBTFT6tS0DqIDh0cMolD1NIKdnFoKxRFMFQFq1OafgltfCQpUnETVyn4H1jBWXCwiFRwcXAQRAcR3Zw6KbhoeN6XVNoi3sfl/Ticc7lcwBtQGSv2AijplpFMxKS11Lrke4OHnlOqZrKooiwK/v276/PR9d5PiFlNu3YQ2U9cl84ul3aeAlN//V3Vn8maGv3f1EGNGRbgkYmVbYsJ3iUeMWgp4qrgvMvHgtMunzuelWSc+JZY0gpqhrhJLKc79HwHl4plrbWD2N6f1VeXxRzqUcxhEyYYilBRgQQF4X/8044/ji1yV2BQLo8CLMpESRETssTz0KFhEjJxCEHqkLhz634PrfvJbW3vFZhtcM4v2tpCAzidoZPV29p4BBgaAG7qTDVUR+qh9uZywPsJMJgChu8os2HmwiF3e38M6Hvh/GMM8B0CdpXzryPO7RqFn4Er/QcXKWq8MSlPPgAAABBJREFUeAEBBQD6/wAIwiX/A7QB7/maltEAAAAASUVORK5CYII=) repeat`,
        heading: t("EP_INDEX_EXPERIENCE_ENTE_TITLE"),
        subheading: t("EP_INDEX_EXPERIENCE_ENTE_DESCRIPTION"),
        icon: "ente",
        language: language,
    };

    const faqItems = [
        {
            question: <Trans i18nKey="EP_INDEX_FAQ_Q_SECURITY" />,
            answer: (
                <p>
                    <Trans
                        i18nKey="EP_INDEX_FAQ_A_SECURITY_1"
                        components={{
                            1: <a href="/blog/cryptography-audit" />,
                        }}
                    />
                </p>
            ),
        },
        {
            question: <Trans i18nKey="EP_INDEX_FAQ_Q_MIGRATION" />,
            answer: (
                <p>
                    <Trans
                        i18nKey="EP_INDEX_FAQ_A_MIGRATION_1"
                        components={{
                            1: (
                                <a href="https://help.ente.io/photos/migration/from-google-photos/" />
                            ),
                            3: (
                                <a href="https://help.ente.io/photos/migration/from-apple-photos/" />
                            ),
                            5: (
                                <a
                                    href={
                                        language === "en"
                                            ? "/download/desktop"
                                            : `/${language}/download/desktop`
                                    }
                                />
                            ),
                        }}
                    />
                </p>
            ),
        },
        {
            question: <Trans i18nKey="EP_INDEX_FAQ_Q_APPS" />,
            answer: (
                <>
                    <p>
                        <Trans i18nKey="EP_INDEX_FAQ_A_APPS_1" />
                    </p>
                    <p>
                        <Trans
                            i18nKey="EP_INDEX_FAQ_A_APPS_2"
                            components={{
                                1: (
                                    <a href="https://github.com/ente-io/ente/tree/main/cli#readme" />
                                ),
                            }}
                        />
                    </p>
                </>
            ),
        },
        {
            question: <Trans i18nKey="EP_INDEX_FAQ_Q_SHARING" />,
            answer: (
                <>
                    <p>
                        <Trans i18nKey="EP_INDEX_FAQ_A_SHARING_1" />
                    </p>
                    <p>
                        <Trans i18nKey="EP_INDEX_FAQ_A_SHARING_2" />
                    </p>
                </>
            ),
        },
        {
            question: <Trans i18nKey="EP_INDEX_FAQ_Q_FAMILY" />,
            answer: (
                <>
                    <p>
                        <Trans i18nKey="EP_INDEX_FAQ_A_FAMILY_1" />
                    </p>
                    <p>
                        <Trans i18nKey="EP_INDEX_FAQ_A_FAMILY_2" />
                    </p>
                </>
            ),
        },
        {
            question: <Trans i18nKey="EP_INDEX_FAQ_Q_ENTE_MEANING" />,
            answer: (
                <p>
                    <Trans
                        i18nKey="EP_INDEX_FAQ_A_ENTE_MEANING"
                        components={{ 1: <a href="/blog/ducky" /> }}
                    />
                </p>
            ),
        },
    ];

    const footerHeading = t("EP_INDEX_FOOTER_DOWNLOADS_TITLE");

    interface CustomerReviewsProps {
        heading: string;
        reviews: IReview[];
    }

    const customerReviewsProps: CustomerReviewsProps = {
        heading: t("EP_INDEX_TESTIMONIALS_TITLE"),
        reviews: reviews.filter((review) => review.lang === language),
    };

    return (
        <Layout
            {...{
                path,
                shouldShowDownloads: true,
                localised: true,
                language: pageContext.language,
                footerHeading: footerHeading,
            }}
        >
            <HeroSection images={[featureImage]} {...heroSectionProps} />
            <MentionsTicker />
            <Features {...FeaturesSectionProps} />
            <WhyEnte {...whyEnteProps} />
            <ExperienceEnte {...experienceEnteProps} />
            <CustomerReviews {...customerReviewsProps} />
            <Pricing />
            <MemoriesPreserved />
            <FAQs faqItems={faqItems} />
        </Layout>
    );
};

export default IndexTemplate;

export const pageQuery = graphql`
    query HeaderImageQuery {
        featureImage: imageSharp(
            fluid: { originalName: { eq: "feature.png" } }
        ) {
            fluid(quality: 70) {
                srcSetWebp
                aspectRatio
            }
        }
        feature1: imageSharp(fluid: { originalName: { eq: "feature-1.png" } }) {
            fluid(quality: 70) {
                srcSetWebp
                aspectRatio
            }
        }
        feature2: imageSharp(fluid: { originalName: { eq: "feature-2.png" } }) {
            fluid(quality: 70) {
                srcSetWebp
                aspectRatio
            }
        }
        feature3: imageSharp(fluid: { originalName: { eq: "feature-3.png" } }) {
            fluid(quality: 70) {
                srcSetWebp
                aspectRatio
            }
        }
        feature4: imageSharp(fluid: { originalName: { eq: "feature-4.png" } }) {
            fluid(quality: 70) {
                srcSetWebp
                aspectRatio
            }
        }
    }
`;
